import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Upload, RotateCcw, RefreshCw } from 'lucide-react';
import { AdminService, supabase } from '../../services/adminService';

const WFHLeadsCSVUpload = () => {
  const [selectedTeamLeadId, setSelectedTeamLeadId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [batchName, setBatchName] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Batch churn UI
  const [batches, setBatches] = useState([]);
  const [selectedBatchName, setSelectedBatchName] = useState('');
  const [batchCounts, setBatchCounts] = useState(null);
  const [batchDispositionCounts, setBatchDispositionCounts] = useState([]);
  const [selectedChurnDispositions, setSelectedChurnDispositions] = useState([]); // [] => all disposed
  const [isChurning, setIsChurning] = useState(false);
  const [isTogglingBatch, setIsTogglingBatch] = useState(false);

  const { data: teamLeads = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['adminTeamLeads'],
    queryFn: AdminService.getTeamLeads,
    staleTime: 5 * 60 * 1000,
  });

  const selectedTeamLeadName = useMemo(() => {
    const tl = teamLeads.find((t) => t.id === selectedTeamLeadId);
    return tl?.name || '';
  }, [teamLeads, selectedTeamLeadId]);

  const selectedBatch = useMemo(() => {
    return (batches || []).find((b) => b.batch_name === selectedBatchName) || null;
  }, [batches, selectedBatchName]);

  const selectedBatchIsActive = useMemo(() => {
    if (!selectedBatch) return null;
    return typeof selectedBatch.is_active === 'boolean' ? selectedBatch.is_active : null;
  }, [selectedBatch]);

  const parseCSV = (csvText) => {
    const sanitize = (val) => {
      const v = (val || '').trim();
      return v.replace(/^"+|"+$/g, '').trim();
    };

    const parseLine = (line) => {
      const out = [];
      let cur = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') {
            cur += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === ',' && !inQuotes) {
          out.push(cur);
          cur = '';
        } else {
          cur += ch;
        }
      }
      out.push(cur);
      return out;
    };

    const lines = csvText.replace(/^\uFEFF/, '').split(/\r?\n/).filter((l) => l.length > 0);
    const headers = parseLine(lines[0]).map((h) => sanitize(h).toLowerCase());

    const requiredColumns = ['name', 'contact_number', 'address', 'car_number', 'car_model', 'pin_code', 'state'];
    const missingColumns = requiredColumns.filter((c) => !headers.includes(c));
    if (missingColumns.length > 0) throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);

    const leads = [];
    for (let i = 1; i < lines.length; i++) {
      const raw = lines[i];
      if (!raw || !raw.trim()) continue;
      const values = parseLine(raw).map(sanitize);
      const get = (key) => values[headers.indexOf(key)] || '';
      leads.push({
        name: get('name'),
        email: get('email') || null,
        contact_number: get('contact_number'),
        address: get('address') || null,
        pin_code: String(get('pin_code') || '').trim() || null,
        state: String(get('state') || '').trim() || null,
        car_model: get('car_model') || null,
        car_number: get('car_number') || null,
      });
    }
    return leads;
  };

  const validateLead = (lead) => {
    if (!lead.name) return 'Missing name';
    if (!lead.contact_number) return 'Missing contact number';
    const digits = (lead.contact_number || '').replace(/\D/g, '');
    if (digits.length !== 10) return 'Contact number must be 10 digits';
    if (!lead.address) return 'Missing address';
    if (!lead.car_number) return 'Missing car number';
    if (!lead.car_model) return 'Missing car model';
    if (!lead.pin_code) return 'Missing pin code';
    if (!lead.state) return 'Missing state';
    return null;
  };

  const loadBatches = async () => {
    if (!selectedTeamLeadId) {
      setBatches([]);
      setSelectedBatchName('');
      setBatchCounts(null);
      setBatchDispositionCounts([]);
      setSelectedChurnDispositions([]);
      return;
    }
    const { data, error } = await supabase.rpc('admin_wfh_list_batches', { p_team_lead_id: selectedTeamLeadId });
    if (error) {
      console.error('Failed to load WFH batches:', error);
      setBatches([]);
      return;
    }
    setBatches(data || []);
  };

  const toggleSelectedBatchActive = async () => {
    if (!selectedTeamLeadId) return alert('Please select a Team Lead');
    if (!selectedBatchName) return alert('Please select a batch');
    if (!selectedBatch) return alert('Batch not found');
    const current = typeof selectedBatch.is_active === 'boolean' ? selectedBatch.is_active : true;
    const next = !current;

    setIsTogglingBatch(true);
    try {
      const { data, error } = await supabase.rpc('admin_wfh_set_batch_active', {
        p_team_lead_id: selectedTeamLeadId,
        p_batch_name: selectedBatchName,
        p_is_active: next,
      });
      if (error) throw error;
      const row = data?.[0];
      alert(row?.message || `Batch set to ${next ? 'active' : 'inactive'}`);
      await loadBatches();
      await loadBatchCounts(selectedBatchName);
      await loadBatchDispositionCounts(selectedBatchName);
    } catch (e) {
      console.error('Failed to toggle batch status:', e);
      alert(e?.message || 'Failed to toggle batch status');
    } finally {
      setIsTogglingBatch(false);
    }
  };

  const loadBatchCounts = async (bn) => {
    if (!selectedTeamLeadId || !bn) {
      setBatchCounts(null);
      return;
    }
    const { data, error } = await supabase.rpc('admin_wfh_batch_counts', {
      p_team_lead_id: selectedTeamLeadId,
      p_batch_name: bn,
    });
    if (error) {
      console.error('Failed to load WFH batch counts:', error);
      setBatchCounts(null);
      return;
    }
    setBatchCounts(data?.[0] || null);
  };

  const loadBatchDispositionCounts = async (bn) => {
    if (!selectedTeamLeadId || !bn) {
      setBatchDispositionCounts([]);
      return;
    }
    const { data, error } = await supabase.rpc('admin_wfh_batch_disposition_counts', {
      p_team_lead_id: selectedTeamLeadId,
      p_batch_name: bn,
    });
    if (error) {
      console.error('Failed to load WFH batch disposition counts:', error);
      setBatchDispositionCounts([]);
      return;
    }
    setBatchDispositionCounts(data || []);
  };

  useEffect(() => {
    loadBatches();
  }, [selectedTeamLeadId]);

  useEffect(() => {
    loadBatchCounts(selectedBatchName);
    loadBatchDispositionCounts(selectedBatchName);
    setSelectedChurnDispositions([]);
  }, [selectedBatchName, selectedTeamLeadId]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }
    if (!selectedTeamLeadId) {
      alert('Please select a Team Lead first');
      return;
    }

    setSelectedFile(file);
    setBatchName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const csvText = event.target.result;
        const leads = parseCSV(csvText);
        setPreviewData(leads.slice(0, 5));
      } catch (error) {
        alert(`Error parsing CSV: ${error.message}`);
        setSelectedFile(null);
        setPreviewData([]);
      }
    };
    reader.readAsText(file);
  };

  const uploadLeads = async () => {
    if (!selectedTeamLeadId) return alert('Please select a Team Lead');
    if (!selectedFile) return alert('Please select a CSV file');

    setIsUploading(true);
    try {
      const csvText = await selectedFile.text();
      const leads = parseCSV(csvText);

      const invalid = leads.find((l) => validateLead(l));
      if (invalid) {
        return alert(`CSV has invalid rows. Example issue: ${validateLead(invalid)}`);
      }

      const { data, error } = await supabase.rpc('admin_bulk_upsert_wfh_leads', {
        p_team_lead_id: selectedTeamLeadId,
        p_batch_name: String(batchName || '').trim() || null,
        p_leads_data: leads,
      });

      if (error) throw error;
      const row = data?.[0];
      alert(row?.message || 'WFH upload complete');

      setSelectedFile(null);
      setPreviewData([]);
      await loadBatches();
      if (batchName) {
        setSelectedBatchName(batchName);
      }
    } catch (e) {
      console.error('WFH upload failed:', e);
      alert(e?.message || 'WFH upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const churnDisposed = async () => {
    if (!selectedTeamLeadId) return alert('Please select a Team Lead');
    if (!selectedBatchName) return alert('Please select a batch');
    const disposedCount = batchCounts?.disposed_count ?? batchCounts?.disposed ?? 0;
    const confirmMsg = disposedCount > 500
      ? `This batch has ~${disposedCount} disposed leads. Churn may take 1-2 minutes. Continue?`
      : 'Reset disposed leads in this batch back to fresh?';
    if (!window.confirm(confirmMsg)) return;

    setIsChurning(true);
    try {
      const { data, error } = await supabase.rpc('admin_wfh_churn_batch_by_disposition', {
        p_team_lead_id: selectedTeamLeadId,
        p_batch_name: selectedBatchName,
        p_dispositions: selectedChurnDispositions.length > 0 ? selectedChurnDispositions : null,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      alert(row?.message || 'Churn complete');
      await loadBatchCounts(selectedBatchName);
      await loadBatchDispositionCounts(selectedBatchName);
      await loadBatches();
    } catch (e) {
      console.error('WFH churn failed:', e);
      const isTimeout = e?.code === '57014' || String(e?.message || '').toLowerCase().includes('timeout');
      alert(
        isTimeout
          ? 'Churn timed out. Deploy supabase/fix-wfh-churn-timeout.sql in Supabase SQL Editor, then retry.'
          : (e?.message || 'WFH churn failed')
      );
    } finally {
      setIsChurning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">WFH Leads Upload</h1>
        <p className="text-gray-600 mt-1">Upload WFH pool leads (separate from normal leads)</p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Team Lead</label>
            <select
              value={selectedTeamLeadId}
              onChange={(e) => setSelectedTeamLeadId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              disabled={isLoadingUsers}
            >
              <option value="">{isLoadingUsers ? 'Loading...' : 'Select Team Lead'}</option>
              {teamLeads.map((tl) => (
                <option key={tl.id} value={tl.id}>
                  {tl.name || tl.email || tl.id}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Batch Name (file name)</label>
            <input
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              placeholder="e.g. wfh_dec_27.csv"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">This is used for batch churn/requeue later.</p>
          </div>

          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">CSV File</label>
            <input type="file" accept=".csv" onChange={handleFileSelect} className="w-full" />
            {selectedFile ? (
              <p className="text-xs text-gray-600 mt-1">
                Selected: <span className="font-medium">{selectedFile.name}</span> (TL: {selectedTeamLeadName || 'N/A'})
              </p>
            ) : null}
          </div>
        </div>

        {previewData.length > 0 ? (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 text-sm font-medium text-gray-700">Preview (first 5 rows)</div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
              {previewData.map((row, idx) => (
                <div key={idx} className="bg-white border border-gray-100 rounded p-2">
                  <div className="font-semibold">{row.name}</div>
                  <div className="text-gray-600">{row.contact_number}</div>
                  <div className="text-gray-600">{row.car_number} • {row.car_model}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <button
          onClick={uploadLeads}
          disabled={isUploading || !selectedFile || !selectedTeamLeadId}
          className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-white ${
            isUploading || !selectedFile || !selectedTeamLeadId ? 'bg-gray-400' : 'bg-primary-500 hover:bg-primary-600'
          }`}
        >
          <Upload size={18} />
          <span>{isUploading ? 'Uploading...' : 'Upload to WFH Pool'}</span>
        </button>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Batch Churn (Requeue)</h2>
            <p className="text-sm text-gray-600">Reset leads in a batch back to fresh/new so TCs can call again (filter by disposition).</p>
            <p className="text-xs text-emerald-700 mt-1">
              Previous telecaller&apos;s saved data (claim time, disposition, notes) is preserved in history,
              so their Employee Report counts and Telecaller Detail page stay intact.
            </p>
          </div>
          <button
            onClick={loadBatches}
            className="inline-flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
          >
            <RefreshCw size={16} />
            <span>Refresh</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Batch</label>
            <select
              value={selectedBatchName}
              onChange={(e) => setSelectedBatchName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              disabled={!selectedTeamLeadId}
            >
              <option value="">{selectedTeamLeadId ? 'Select batch' : 'Select Team Lead first'}</option>
              {batches.map((b) => (
                <option key={b.batch_name} value={b.batch_name}>
                  {b.batch_name} {b.is_active === false ? '[inactive]' : ''} (disposed: {b.disposed_count}/{b.total_count})
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center justify-between gap-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Counts</label>
              {selectedBatchName ? (
                <div className="flex items-center gap-2">
                  {selectedBatchIsActive !== null ? (
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        selectedBatchIsActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                      title={selectedBatchIsActive ? 'Active batch: new claims allowed' : 'Inactive batch: new claims blocked'}
                    >
                      {selectedBatchIsActive ? 'Active' : 'Inactive'}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={toggleSelectedBatchActive}
                    disabled={!selectedBatchName || !selectedTeamLeadId || isTogglingBatch}
                    className="px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-sm disabled:opacity-50"
                  >
                    {isTogglingBatch
                      ? 'Updating...'
                      : selectedBatchIsActive === false
                        ? 'Activate Batch'
                        : 'Deactivate Batch'}
                  </button>
                </div>
              ) : null}
            </div>
            <div className="px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800">
              {batchCounts ? (
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  <div><span className="font-semibold">Total:</span> {batchCounts.total_count}</div>
                  <div><span className="font-semibold">Disposed:</span> {batchCounts.disposed_count}</div>
                  <div><span className="font-semibold">Fresh:</span> {batchCounts.fresh_count}</div>
                  <div><span className="font-semibold">Assigned:</span> {batchCounts.assigned_count}</div>
                </div>
              ) : (
                <div className="text-gray-600">Select a batch to see counts.</div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-gray-900">Churn by Disposition</div>
              <div className="text-xs text-gray-600">Select dispositions to reset. If none selected, all disposed leads will be reset.</div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSelectedChurnDispositions([])}
                className="px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-sm"
                disabled={!selectedBatchName}
              >
                All Disposed
              </button>
              <button
                type="button"
                onClick={() => setSelectedChurnDispositions((batchDispositionCounts || []).map((d) => String(d.disposition || '')))}
                className="px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-sm"
                disabled={!selectedBatchName}
              >
                Select All
              </button>
            </div>
          </div>

          {selectedBatchName ? (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
              {(batchDispositionCounts || []).length > 0 ? (
                batchDispositionCounts.map((d) => {
                  const key = String(d.disposition || '');
                  const isChecked = selectedChurnDispositions.includes(key);
                  return (
                    <label key={key} className="flex items-center gap-2 text-sm text-gray-800">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedChurnDispositions((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(key);
                            else next.delete(key);
                            return Array.from(next);
                          });
                        }}
                      />
                      <span className="font-medium">{key}</span>
                      <span className="text-gray-500">({d.count})</span>
                    </label>
                  );
                })
              ) : (
                <div className="text-sm text-gray-600 md:col-span-3">No dispositions found for this batch (nothing to churn).</div>
              )}
            </div>
          ) : (
            <div className="mt-3 text-sm text-gray-600">Select a batch to see disposition breakdown.</div>
          )}
        </div>

        <button
          onClick={churnDisposed}
          disabled={isChurning || !selectedBatchName || !selectedTeamLeadId}
          className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-white ${
            isChurning || !selectedBatchName || !selectedTeamLeadId ? 'bg-gray-400' : 'bg-amber-500 hover:bg-amber-600'
          }`}
        >
          <RotateCcw size={18} />
          <span>
            {isChurning
              ? 'Churning...'
              : selectedChurnDispositions.length > 0
                ? 'Churn Done (Reset Selected Dispositions → Fresh)'
                : 'Churn Done (Reset All Disposed → Fresh)'}
          </span>
        </button>
      </div>
    </div>
  );
};

export default WFHLeadsCSVUpload;


